import { useCallback, useContext, useState } from 'react';

import { doRequest } from '@r1-frontend/do-request';
import { IResponseDto } from '@r1-frontend/api-domru/api-addresses/v1/dto/ResponseDto';
import suggest from '@r1-frontend/api-domru/api-addresses/v1/suggest';

import { selectAddressFromList, setAddress } from '@r1-frontend/unified-address-form/actions';
import { Ctx } from '@r1-frontend/unified-address-form/Ctx';
import getErrorBySelectedSuggestion from '@r1-frontend/unified-address-form/helpers/getErrorBySelectedSuggestion';
import { useCtxDispatch, useCtxSelector } from '@r1-frontend/unified-address-form/hooks';
import { TOwnSuggestion } from '@r1-frontend/unified-address-form/reducer';
import getAddressState from '@r1-frontend/unified-address-form/selectors/getAddressState';

import { Paragraph5 } from '@r1-frontend/ui-react/components/typography/paragraph';
import Autocomplete from '@r1-frontend/ui-react/organisms/Forms/PersonForm/Autocomplete';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { useOnceExecute } from '@r1-frontend/shared/hooks/useOnceExecute';

interface IProps {
    cityName?: string,
    withoutFlat?: boolean,
}

/**
 * Компонент ввода адреса
 * @param withoutFlat - не требовать ввода квартиры
 */
const AddressInput = ({ withoutFlat }: IProps): JSX.Element => {
    const { analytics } = useContext(Ctx);
    const dispatch = useCtxDispatch();
    const {
        selectedAddress,
        isLoading,
    } = useCtxSelector(getAddressState);
    const [suggestions, setSuggestions] = useState<Record<string, TOwnSuggestion>>({});

    const analyticsInputStarted = useOnceExecute(() => analytics.inputStarted());

    const handleOnLoadSuggestions = useCallback(async(value) => {
        const resp = await doRequest.r1PlatformGateway<IResponseDto>(
            suggest(value),
        );

        const processedSuggestions = resp.isSuccess
            ? resp.payload.suggestions.reduce<Record<string, TOwnSuggestion>>((acc, cur) => {
                acc[cur.value] = {
                    city: cur.data?.city,
                    street: cur.data?.street,
                    house: cur.data?.house,
                    flat: cur.data?.flat,
                    ownData: {
                        unifiedHouseId: cur.data?.erth?.unified_house_id,
                        houseId: cur.data?.erth?.house_id,
                    },
                };
                return acc;
            }, {})
            : {};
        setSuggestions(processedSuggestions);
        return Object.keys(processedSuggestions);
    }, []);

    const handleSelectAddress = (value: string) => {
        analyticsInputStarted();
        dispatch(setAddress(value));
        if (value in suggestions) {
            analytics.selectAddress();
            dispatch(selectAddressFromList(getErrorBySelectedSuggestion(value, suggestions, !!withoutFlat).hasError));
        }
    };

    const error = getErrorBySelectedSuggestion(selectedAddress, suggestions, !!withoutFlat);

    return <>
        <Autocomplete
            placeholder={`Город, улица, дом${withoutFlat ? '' : ', квартира'}`}
            value={selectedAddress}
            onChange={handleSelectAddress}
            onLoadSuggestions={handleOnLoadSuggestions}
            throttleValue={25}
            isDisabled={isLoading}
        />
        {error.hasError && (
            <Paragraph5 $color={COLORS.Error} style={{ width: '100%' }}>
                {error.message}
            </Paragraph5>
        )}
    </>;
};

export default AddressInput;
